import {
    Editor,
    Range,
    Transforms,
    Element as SlateElement,
    Node,
    Point,
    Text,
} from "slate";

// Non-verbal action tags (e.g. {laughs}, {sighs}) are inline VOID nodes that live in
// the block's text flow — rendered as a chip much like `pause` shows its duration.
//
// They are constrained to the very start or very end of a block: the `{` picker only
// fires at a block edge, and the caret is contained so text can't be typed outside the
// leading/trailing runs of chips. The backend reads these nodes out of slate_value and
// emits `{action}` into the text it feeds the model.

export const NONVERBAL_TYPE = "nonverbal";


export const CURATED_ACTIONS = [
    "laughing",
    "sigh",
    "Uhm",
    "Shh",
    "Question-ah",
    "Question-ei",
    "Question-en",
    "Question-oh",
    "Surprise-wa",
    "Surprise-yo",
    "Dissatisfaction-hnn",
];

export const isNonverbal = (n) =>
    SlateElement.isElement(n) && n.type === NONVERBAL_TYPE;

// Only the emotional engine interprets {..} sound tags, so the picker is offered only
// on blocks using an emotional-engine voice (matches the emotion feature's gate).
const NONVERBAL_ENGINE = "emotional";

export const engineSupportsNonverbal = (engine) => engine === NONVERBAL_ENGINE;

export const blockSupportsNonverbal = (editor) => {
    const { selection } = editor;
    if (!selection) return false;
    try {
        const block = Node.get(editor, [selection.anchor.path[0]]);
        return engineSupportsNonverbal(block?.audio_profile?.engine);
    } catch (e) {
        return false;
    }
};

export const insertNonverbal = (editor, action) => {
    const value = (action || "").trim();
    if (!value || !editor.selection) return;
    const node = { type: NONVERBAL_TYPE, action: value, children: [{ text: "" }] };
    Transforms.insertNodes(editor, node);
    // Step the caret past the inserted void so the chip stays pinned to the edge and
    // any following text lands on the correct side of it.
    Transforms.move(editor);
};

// Insert a chip at a specific block edge regardless of where the caret is. Used by the
// toolbar "Sound" button (which has no caret to rely on, unlike the `{` picker).
export const insertNonverbalAtEdge = (editor, blockPath, edge, action) => {
    const value = (action || "").trim();
    if (!value) return;
    const bounds = getWindowBounds(editor, blockPath);
    if (!bounds) return;
    Transforms.select(editor, edge === "start" ? bounds.start : bounds.end);
    Transforms.insertNodes(editor, {
        type: NONVERBAL_TYPE,
        action: value,
        children: [{ text: "" }],
    });
    Transforms.move(editor);
};

// Slate pads inline voids with empty text nodes, so a leading chip is NOT children[0]
// — the run looks like [{text:""}, chip, chip, {text:""}, "real text"]. These two
// helpers find the leading/trailing chip runs while stepping over that empty padding.

const isEmptyText = (n) => Text.isText(n) && n.text.length === 0;

// Index of the LAST chip in the leading run (chips + empty padding from the start),
// or -1 if the paragraph doesn't start with a chip.
const getLeadingChipIndex = (para) => {
    let last = -1;
    for (let i = 0; i < para.children.length; i++) {
        const child = para.children[i];
        if (isNonverbal(child)) last = i;
        else if (isEmptyText(child)) continue;
        else break; // real text or another inline (e.g. pause) — run ends
    }
    return last;
};

// Index of the FIRST chip in the trailing run (chips + empty padding from the end),
// or -1 if the paragraph doesn't end with a chip.
const getTrailingChipIndex = (para) => {
    let first = -1;
    for (let i = para.children.length - 1; i >= 0; i--) {
        const child = para.children[i];
        if (isNonverbal(child)) first = i;
        else if (isEmptyText(child)) continue;
        else break;
    }
    return first;
};

// Start of the editable window: the point just after the last leading chip.
const getWindowStart = (editor, blockPath) => {
    const firstParaPath = [...blockPath, 0];
    const firstPara = Node.get(editor, firstParaPath);
    const last = getLeadingChipIndex(firstPara);
    if (last === -1) return Editor.start(editor, blockPath);
    return (
        Editor.after(editor, [...firstParaPath, last]) ||
        Editor.end(editor, blockPath)
    );
};

// End of the editable window: the point just before the first trailing chip.
const getWindowEnd = (editor, blockPath) => {
    const block = Node.get(editor, blockPath);
    const lastParaPath = [...blockPath, block.children.length - 1];
    const lastPara = Node.get(editor, lastParaPath);
    const first = getTrailingChipIndex(lastPara);
    if (first === -1) return Editor.end(editor, blockPath);
    return (
        Editor.before(editor, [...lastParaPath, first]) ||
        Editor.start(editor, blockPath)
    );
};

export const getWindowBounds = (editor, blockPath) => {
    try {
        return {
            start: getWindowStart(editor, blockPath),
            end: getWindowEnd(editor, blockPath),
        };
    } catch (e) {
        return null;
    }
};

// Returns 'start' | 'end' | null — which block edge the collapsed caret sits at.
// This is where a `{` press is allowed to open the picker.
export const getNonverbalEdge = (editor) => {
    const { selection } = editor;
    if (!selection || !Range.isCollapsed(selection)) return null;
    const blockPath = [selection.anchor.path[0]];
    const bounds = getWindowBounds(editor, blockPath);
    if (!bounds) return null;
    if (Point.equals(selection.anchor, bounds.start)) return "start";
    if (Point.equals(selection.anchor, bounds.end)) return "end";
    return null;
};

// ─── Containment guards ───────────────────────────────────────────────────────
// The chip lives in the text flow, so the caret can wander next to it. These helpers
// fence off the spots where wandering would strand a chip mid-text.

// Snapshot the caret's relationship to its block's editable window. Used by the
// key/selection guards. Returns null when there's nothing to guard (no collapsed
// caret, or the block has no chips).
export const getBlockGuardContext = (editor) => {
    const { selection } = editor;
    if (!selection || !Range.isCollapsed(selection)) return null;
    const blockPath = [selection.anchor.path[0]];
    const bounds = getWindowBounds(editor, blockPath);
    if (!bounds) return null;

    let blockStart;
    let blockEnd;
    try {
        blockStart = Editor.start(editor, blockPath);
        blockEnd = Editor.end(editor, blockPath);
    } catch (e) {
        return null;
    }

    return {
        blockPath,
        bounds,
        atWindowStart: Point.equals(selection.anchor, bounds.start),
        atWindowEnd: Point.equals(selection.anchor, bounds.end),
        hasLeading: !Point.equals(bounds.start, blockStart),
        hasTrailing: !Point.equals(bounds.end, blockEnd),
    };
};

// If the collapsed caret ended up outside the window (e.g. from a click or an
// up/down arrow), pull it back to the nearest window edge. Returns true if it moved.
export const clampCaretToWindow = (editor) => {
    const { selection } = editor;
    if (!selection || !Range.isCollapsed(selection)) return false;
    const blockPath = [selection.anchor.path[0]];
    const bounds = getWindowBounds(editor, blockPath);
    if (!bounds) return false;

    const caret = selection.anchor;
    if (Point.isBefore(caret, bounds.start)) {
        Transforms.select(editor, bounds.start);
        return true;
    }
    if (Point.isAfter(caret, bounds.end)) {
        Transforms.select(editor, bounds.end);
        return true;
    }
    return false;
};

// Path of the chip adjacent to the editable text at the given edge — the one a
// boundary Backspace/Delete should remove. Null when there's no chip there.
export const getAdjacentChipPath = (editor, edge) => {
    const { selection } = editor;
    if (!selection) return null;
    const blockPath = [selection.anchor.path[0]];
    let block;
    try {
        block = Node.get(editor, blockPath);
    } catch (e) {
        return null;
    }

    if (edge === "start") {
        const paraPath = [...blockPath, 0];
        const para = Node.get(editor, paraPath);
        const last = getLeadingChipIndex(para);
        return last === -1 ? null : [...paraPath, last];
    }

    const lastParaPath = [...blockPath, block.children.length - 1];
    const para = Node.get(editor, lastParaPath);
    const first = getTrailingChipIndex(para);
    return first === -1 ? null : [...lastParaPath, first];
};

// Normalizer check: is this chip in a valid leading/trailing run, or stranded
// interior (e.g. dropped mid-text by a paste)?
export const isNonverbalAtValidEdge = (editor, path) => {
    if (path.length !== 3) return true;
    const inlineIndex = path[2];
    let para;
    let block;
    try {
        para = Node.get(editor, [path[0], path[1]]);
        block = Node.get(editor, [path[0]]);
    } catch (e) {
        return true;
    }

    const isFirstPara = path[1] === 0;
    const isLastPara = path[1] === block.children.length - 1;

    // Valid if this chip is within the leading run of the first paragraph or the
    // trailing run of the last paragraph (empty-text padding stepped over by the
    // run helpers).
    if (isFirstPara && inlineIndex <= getLeadingChipIndex(para)) return true;
    const trailingStart = getTrailingChipIndex(para);
    if (isLastPara && trailingStart !== -1 && inlineIndex >= trailingStart) return true;
    return false;
};
